import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components/macro';
import HtmlParser from 'html-react-parser';
import Slider from 'react-slick';

import { palette, breakPoint } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

import IconUser from '@mirinae/members/components/assets/icon-user.svg';

import Localization from '@mirinae/shared/il8n';

const Wrapper = styled.div`
    ${os.isOthers ? `
        // desktop infinite loop
        @keyframes slideAnim {
            from {
                transform: translateX(0%);
            }
            to {
                transform: translateX(-100%);
            }
        }
        .looper {
            width: 100%;
            overflow: hidden;
        }
        .looper + .looper {
            margin-top: 20px;
        }
        .looper__innerList {
            display: flex;
            justify-content: center;
            width: fit-content;
        }
        .looper__innerList[data-animate="true"] .looper__listInstance {
            animation: slideAnim linear infinite;   
        }
        .looper__listInstance {
            display: flex;
            width: max-content;
            animation: none;
        }
    ` : `
        overflow: hidden;
        padding: 0 20px;

        // react-slick
        .slick-slider {
            position: relative;
            margin-right: -5px;
            padding-left: 10px;
            .slick-track {
                display: flex;
                gap: 0 10px;
            }
            .slick-list {
                width: calc(100% + 11px);
                padding-bottom: 5px;
                margin: 0 0 -5px -8px;
            }
            .slick-prev,
            .slick-next {
                position: absolute;
                top: 0;
                bottom: 0;
                width: 30px;
                font-size: 0;
                z-index: 100;
                background: url(/assets/members/images/arrow-slider.png) no-repeat 50% 50%/100%;
                :before {
                    content: '';
                }
            }
            .slick-prev {
                left: -14px;
                top: 50%;
            }
            .slick-next {
                right: -14px;
                top: 50%;
                transform: rotate(-180deg);
            }
        }
    `}
`;
const Title = styled.div`
    margin-bottom: 35px;
    text-align: center;
    strong {
        display: block;
        margin-bottom: 15px;
        color: ${palette.pro.darken};
        font-size: 24px;
        font-weight: 700;
    }
    p {
        line-height: 23px;
        font-size: 16px;
        font-weight: 400;
        color: ${palette.gray[3]};
    }
`;
const Block = styled.div`
    display: flex !important;
    flex-direction: column;
    gap: 12px 0;
    width: 463px;
    height: 216px;
    margin-right: 20px;
    padding: 25px;
    border-radius: 10px;
    background-color: ${props => (props.theme)};

    strong {
        display: block;
        height: 17px;
        line-height: 17px;
        font-size: 16px;
        font-weight: 700;
        color: ${palette.pro.darken};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    p {
        height: 75px;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: ${palette.gray[1]};
        overflow: hidden;
    }

    @media only screen and (max-width: ${breakPoint.common.lg}) {
        p {
            height: 93px;
        }
    }
`;
const User = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px 0;
    justify-content: center;
    position: relative;
    height: 50px;
    padding-left: 65px;
    :before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        display: block;
        width: 50px;
        height: 50px;
        border-radius: 15px;
        background: ${props => (props.color)} url(${IconUser}) no-repeat 50%;
    }
    b {
        display: block;
        height: 17px;
        line-height: 17px;
        color: ${palette.gray[2]};
        font-size: 12px;
        font-weight: 700;
    }
    span {
        display: block;
        height: 17px;
        line-height: 17px;
        color: ${palette.pro.darken};
        font-weight: 300;
        font-size: 12px;
    }
`;

const InfiniteLooper = function InfiniteLooper({
    speed, // number
    direction, // "right" | "left"
    children, // React.ReactNode
}) {
    const [looperInstances, setLooperInstances] = useState(1);
    const outerRef = useRef(null);
    const innerRef = useRef(null);

    function resetAnimation() {
        if (innerRef?.current) {
            innerRef.current.setAttribute('data-animate', 'false');

            setTimeout(() => {
                if (innerRef?.current) {
                    innerRef.current.setAttribute('data-animate', 'true');
                }
            }, 10);
        }
    }

    const setupInstances = useCallback(() => {
        if (!innerRef?.current || !outerRef?.current) return;

        const { width } = innerRef.current.getBoundingClientRect();

        const { width: parentWidth } = outerRef.current.getBoundingClientRect();

        const widthDeficit = parentWidth - width;

        const instanceWidth = width / innerRef.current.children.length;

        if (widthDeficit) {
            setLooperInstances(
                looperInstances + Math.ceil(widthDeficit / instanceWidth) + 1,
            );
        }

        resetAnimation();
    }, [looperInstances]);

    useEffect(() => setupInstances(), [setupInstances]);

    useEffect(() => {
        window.addEventListener('resize', setupInstances);

        return () => {
            window.removeEventListener('resize', setupInstances);
        };
    }, [looperInstances, setupInstances]);

    return (
        <div className="looper" ref={outerRef}>
            <div className="looper__innerList" ref={innerRef} data-animate="true">
                {[...Array(looperInstances)].map((_, ind) => (
                    <div
                        key={ind}
                        className="looper__listInstance"
                        style={{
                            animationDuration: `${speed}s`,
                            animationDirection: direction === 'right' ? 'reverse' : 'normal',
                        }}
                    >
                        {children}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SliderSttings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
};

const Reviews = () => {
    const datas = [
        {
            title: Localization['plan-review-Tbafs'],
            text: Localization['plan-review-HtabregtyebPasipatgmaf'],
            color: ['#FFF3F3', '#FFAFAF'],
            name: 'julio_franco',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-OotbKla'],
            text: Localization['plan-review-ItaloKdasaatiDootb'],
            color: ['#F2F9FF', '#A5D6D3'],
            name: 'joshzheng77',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-AKld'],
            text: Localization['plan-review-FtabyKsNtanhltlashilg'],
            color: ['#FFF5E4', '#F1D572'],
            name: 'Sho-Yuri',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-OomfKla'],
            text: Localization['plan-review-IihaetmmpilKmeaaTy'],
            color: ['#EAECFF', '#E4B1F7'],
            name: 'Andrew Jones',
            from: Localization['plan-review-Pr'],
        },
        {
            title: Localization['plan-review-너좋것같'],
            text: Localization['plan-review-YceaasLaveauAtaefsak'],
            color: ['#F5FFF6', '#A0C991'],
            name: 'Rachael Adhiambo',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-A'],
            text: Localization['plan-review-IttiaaaIteidaiavutIhsooataalanl'],
            color: ['#EAECFF', '#E4B1F7'],
            name: 'Disha Upreti',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-AMhfKl'],
            text: Localization['plan-review-TaidmhfaKlTgcAhsmmtTgcish'],
            color: ['#FFF5E4', '#F1D572'],
            name: 'Ruiii',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-EflK'],
            text: Localization['plan-review-MaiasuapftlmIsposeslcsKbi'],
            color: ['#F2F9FF', '#A5D6D3'],
            name: 'Rabbit07',
            from: Localization['plan-review-Asr'],
        },
        {
            title: Localization['plan-review-TaiaflK'],
            text: Localization['plan-review-IwIhtwIflKbiaufrmIlhtgieatsaisc'],
            color: ['#FFF3F3', '#FFAFAF'],
            name: 'Joe',
            from: Localization['plan-review-TMT'],
        },
        {
            title: Localization['plan-review-AflK'],
            text: Localization['plan-review-IwIhtwIflKbiaufrmIlhtgieatsaisc'],
            color: ['#F5FFF6', '#A0C991'],
            name: 'Mariell',
            from: Localization['plan-review-TMt'],
        },
    ];

    return (
        <Wrapper>
            <Title>
                <strong>{Localization['plan-review-R']}</strong>
                <p>{Localization['plan-review-TaRoos']}</p>
            </Title>

            {os.isOthers ? (
                <>
                    <InfiniteLooper speed="120" direction="right">
                        {datas.map((d, i) => (
                            <Block key={i} style={{ backgroundColor: d.color[0] }}>
                                <strong>{d.title}</strong>
                                <p>{HtmlParser(d.text)}</p>
                                <User color={d.color[1]}>
                                    <b>{d.name}</b>
                                    <span>{d.from}</span>
                                </User>
                            </Block>
                        ))}
                    </InfiniteLooper>

                    <InfiniteLooper speed="100" direction="left">
                        {datas.map((d, i) => (
                            <Block key={i} style={{ backgroundColor: d.color[0] }}>
                                <strong>{d.title}</strong>
                                <p>{HtmlParser(d.text)}</p>
                                <User color={d.color[1]}>
                                    <b>{d.name}</b>
                                    <span>{d.from}</span>
                                </User>
                            </Block>
                        ))}
                    </InfiniteLooper>
                </>
            ) : (
                <Slider {...SliderSttings}>
                    {datas.map((d, i) => (
                        <Block key={i} theme={d.color[0]}>
                            <strong>{d.title}</strong>
                            <p>{HtmlParser(d.text)}</p>
                            <User color={d.color[1]}>
                                <b>{d.name}</b>
                                <span>{d.from}</span>
                            </User>
                        </Block>
                    ))}
                </Slider>
            )}
        </Wrapper>
    );
};

export default Reviews;